import {
  Autocomplete,
  Box,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  DeleteButton,
  autocompletePopupIcon,
  autocompleteSx,
} from '@modules/_shared/components/filters/advanced-filters/DeleteButton';
import { Node } from '@modules/_shared/components/filters/advanced-filters/Node';
import {
  addWhereClause,
  deleteWhereClause,
} from '@modules/_shared/components/filters/Filters';
import { useSchema } from '@modules/_shared/components/filters/useSchema';

interface InputObjectProps {
  name: string;
  node: any;
  path: string[];
  where: any;
  setWhere: (where: any) => void;
}

export const InputObject = ({
  name,
  node,
  path,
  where,
  setWhere,
}: InputObjectProps) => {
  const { getTypeFromPath } = useSchema();

  const sanitizedPath = !isNaN(parseInt(name)) ? path.slice(0, -1) : path;
  const type = getTypeFromPath(sanitizedPath);

  if (type?.kind !== 'INPUT_OBJECT') {
    throw new Error('Type is not an input object');
  }

  const options = type?.inputFields ?? [];

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        <Tooltip
          title={
            <>
              {type?.name}
              <br />
              {type?.kind}
            </>
          }
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            {name}
          </Typography>
        </Tooltip>
        {name !== 'where' && (
          <DeleteButton
            onClick={() => {
              const newWhere = deleteWhereClause(where, path);
              setWhere(newWhere);
            }}
          />
        )}
      </Stack>
      <Stack
        sx={{
          gap: 0.75,
          pl: 1.5,
          ml: 0.5,
          mt: 0.5,
          borderLeft: '2px solid',
          borderColor: 'grey.200',
        }}
      >
        {Object.keys(node ?? {}).map(key => (
          <Node
            key={[...path, key].join('.')}
            name={key}
            node={node[key]}
            path={[...path, key]}
            where={where}
            setWhere={setWhere}
          />
        ))}
        <Autocomplete
          value={null as any}
          options={options}
          disableClearable
          clearOnBlur
          blurOnSelect
          popupIcon={autocompletePopupIcon}
          sx={{ width: 280, ...autocompleteSx }}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={`+ Add filter (${name})`}
              size="small"
            />
          )}
          size="small"
          getOptionLabel={option => option.name}
          onChange={(e, field) => {
            const fieldName = field?.name;
            const typeKind = field?.type?.kind;
            const typeName = field?.type?.name;

            if (!fieldName || !typeKind) {
              throw new Error('Invalid field');
            }

            let filterValue: any;
            if (['INPUT_OBJECT', 'NON_NULL'].includes(typeKind)) {
              filterValue = {};
            } else if (typeKind === 'LIST') {
              filterValue = [];
            } else if (typeKind === 'ENUM') {
              filterValue = undefined;
            } else if (typeKind === 'SCALAR') {
              switch (typeName) {
                case 'String':
                  filterValue = '';
                  break;
                case 'Int':
                case 'Float':
                case 'float8':
                  filterValue = 0;
                  break;
                case 'Boolean':
                  filterValue = true;
                  break;
                case 'date':
                case 'timestamptz':
                  filterValue = new Date();
                  break;
                case 'geometry':
                  filterValue = { type: 'Point', coordinates: [0, 0] };
                  break;
                default:
                  filterValue = undefined;
                  break;
              }
            } else {
              throw new Error('Unknown typeKind: ' + typeKind);
            }
            const newWhere = addWhereClause(
              where,
              [...path, fieldName],
              filterValue,
            );
            setWhere(newWhere);
          }}
        />
      </Stack>
    </Box>
  );
};
